<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('Profile|Custom Report') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <p>
              {{
                s__(
                  'Profile|Custom reports allow you to create personalized task reports with specific filters and settings. You can configure what tasks to include, how they are organized, and access the report quickly using the /my command in Direct Messages.'
                )
              }}
            </p>

            <h4>{{ s__('Profile|How to Access') }}</h4>
            <p>
              {{
                s__(
                  'Profile|Once configured, you can access your custom report at any time by sending the /my command in a Direct Message with the bot. The bot will immediately generate and send you a report based on your saved configuration.'
                )
              }}
            </p>

            <h4>{{ s__('Profile|Configuration Options') }}</h4>
            <p>
              {{
                s__(
                  'Profile|You can configure your custom report with various filters and options:'
                )
              }}
            </p>
            <ul style="margin: 0 0 16px 0; padding-left: 20px; color: #666">
              <li style="margin-bottom: 8px">
                {{ s__('Profile|Filter by task status (open, in progress, completed, etc.)') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('Profile|Filter by your role (author, assignee, subscriber)') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('Profile|Filter by project or view tasks across all projects') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('Profile|Filter by personal labels or task types') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('Profile|Group tasks in swimlanes by assignee, status, or other criteria') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('Profile|Sort tasks by various criteria (due date, priority, etc.)') }}
              </li>
            </ul>

            <h4>{{ s__('Profile|Example Configurations') }}</h4>

            <div style="margin-bottom: 16px">
              <strong style="color: #333">{{ s__('Profile|Example 1: My Open Tasks') }}</strong>
              <p style="margin: 4px 0 0 0; color: #666">
                {{
                  s__(
                    'Profile|Show all open tasks where I am the assignee, sorted by due date. Perfect for daily task review.'
                  )
                }}
              </p>
            </div>

            <div style="margin-bottom: 16px">
              <strong style="color: #333">{{
                s__('Profile|Example 2: Tasks Due This Week')
              }}</strong>
              <p style="margin: 4px 0 0 0; color: #666">
                {{
                  s__(
                    'Profile|Show all tasks due within the next 7 days, across all projects, sorted by priority. Great for weekly planning.'
                  )
                }}
              </p>
            </div>

            <div style="margin-bottom: 16px">
              <strong style="color: #333">{{
                s__('Profile|Example 3: High Priority Review')
              }}</strong>
              <p style="margin: 4px 0 0 0; color: #666">
                {{
                  s__(
                    'Profile|Show all high priority tasks where I am the author or assignee, from specific projects, sorted by creation date.'
                  )
                }}
              </p>
            </div>

            <h4>{{ s__('Profile|Quick Command') }}</h4>
            <p>
              {{
                s__(
                  'Profile|After configuring your report, simply send /my in a Direct Message with the bot to instantly receive your personalized report. The report will be formatted and sent directly to you.'
                )
              }}
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('Profile|Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from '../common/icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 20px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}

.info-section ul {
  line-height: 1.6;
}
</style>
